import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router";

import dlrLogo from "src/assets/dlr-logo.png";
import { DEV_LOGIN_AVAILABLE, dxmSsoConfig } from "src/auth/dxmSsoConfig";
import { SignInButton } from "src/auth/SignInButton";
import { useSession } from "src/auth/useSession";
import { ProductTour } from "src/pages/SignInPage/ProductTour";
import { RegistrationCard } from "src/pages/SignInPage/RegistrationCard";
import { ROUTES } from "src/routes/paths";
import { usePageTitle } from "src/routes/usePageTitle";

const LOGO_WIDTH = 120;
const LOGO_HEIGHT = 28;
const CARD_WIDTH = 460;
const PAGE_MAX_WIDTH = 1200;

/**
 * Shown to every signed-out visitor (there is no anonymous content). Wireframe 2:635 "Create
 * Account" layout with the Desktop v3 D02/D03 card: the product story on the left, registration on
 * the right; stacked on phones with the card first.
 */
export const SignInPage: FC = () => {
	const { t } = useTranslation();
	const { signInEnabled, devLoginEnabled, signInLinkMode } = useSession();
	usePageTitle(t("register.pageTitle"));

	const ssoAvailable = signInEnabled && Boolean(dxmSsoConfig);
	const signIn = ssoAvailable ? (
		<SignInButton variant="link" />
	) : DEV_LOGIN_AVAILABLE && devLoginEnabled ? (
		<Link component={RouterLink} to={ROUTES.devLogin} variant="subtitle2">
			{t("devLogin.link")}
		</Link>
	) : undefined;

	return (
		<Box sx={{ minHeight: "100dvh", bgcolor: "background.default" }}>
			<Box component="header" sx={{ px: { xs: 2, md: 3 }, py: 2.5, display: "flex", alignItems: "center", gap: 1.5 }}>
				<img src={dlrLogo} alt="Digital Realty" width={LOGO_WIDTH} height={LOGO_HEIGHT} />
				<Typography color="primary" sx={{ fontSize: 20, fontWeight: 500 }}>
					{t("app.title")}
				</Typography>
			</Box>
			<Box
				component="main"
				sx={{
					maxWidth: PAGE_MAX_WIDTH,
					mx: "auto",
					px: { xs: 2, md: 4 },
					pb: 6,
					pt: { xs: 1, md: 4 },
					display: "grid",
					gap: { xs: 4, md: 8 },
					alignItems: "start",
					gridTemplateColumns: { xs: "minmax(0, 1fr)", md: `minmax(0, 1fr) ${CARD_WIDTH}px` }
				}}>
				<Stack spacing={2} sx={{ order: { xs: 2, md: 1 } }}>
					<Typography variant="h1" sx={{ fontSize: { xs: 28, md: 36 }, lineHeight: 1.2 }}>
						{t("register.headline")}
					</Typography>
					<Typography variant="h3" component="p">
						{t("register.tagline")}
					</Typography>
					<Typography variant="body1" sx={{ color: "brand.body", maxWidth: 560 }}>
						{t("register.body")}
					</Typography>
					<Box sx={{ pt: 2, maxWidth: 560 }}>
						<ProductTour />
					</Box>
				</Stack>
				<Box sx={{ order: { xs: 1, md: 2 } }}>
					{/* Without email sign-in links (no DXM SSO M2M app), SSO alone is a working sign-in. */}
					{signInLinkMode ? (
						<RegistrationCard signIn={signIn} />
					) : ssoAvailable ? (
						<SignInButton variant="contained" />
					) : (
						<Stack spacing={2}>
							{signIn}
							<Alert severity="warning">{t("auth.notConfigured")}</Alert>
						</Stack>
					)}
				</Box>
			</Box>
		</Box>
	);
};
